import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material';

interface BlurryOverlayProps {
  loading: boolean;
  transitionDuration?: number; // in milliseconds
}

const BlurryOverlay = ({ 
  loading, 
  transitionDuration = 800 
}: BlurryOverlayProps) => {
  const theme = useTheme();
  const [show, setShow] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Handle mounting/unmounting with delay for exit animation
  useEffect(() => {
    if (loading && !mounted) {
      setMounted(true);
      // Small delay to ensure DOM is ready before starting animation
      setTimeout(() => setShow(true), 50);
    } else if (!loading && mounted) {
      setShow(false);
      // Keep component mounted until exit animation completes
      setTimeout(() => setMounted(false), transitionDuration);
    }
  }, [loading, mounted, transitionDuration]);

  if (!mounted) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: show ? 'blur(8px)' : 'blur(0px)',
        WebkitBackdropFilter: show ? 'blur(8px)' : 'blur(0px)',
        opacity: show ? 1 : 0,
        pointerEvents: show ? 'auto' : 'none',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: theme.zIndex.modal + 1,
        transition: `
          opacity ${transitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1),
          backdrop-filter ${transitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1)
        `,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          transform: show ? 'scale(1)' : 'scale(0.8)',
          opacity: show ? 1 : 0,
          transition: `
            transform ${transitionDuration}ms cubic-bezier(0.34, 1.56, 0.64, 1),
            opacity ${transitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1)
          `,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Primary CircularProgress */}
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: theme.palette.primary.main,
            filter: 'drop-shadow(0 0 8px rgba(25, 118, 210, 0.5))',
          }}
        />
        
        {/* Secondary CircularProgress for 3D effect */}
        <CircularProgress
          size={40}
          thickness={4}
          sx={{
            position: 'absolute',
            color: theme.palette.primary.light,
            opacity: 0.7,
          }}
        />
      </Box>
    </Box>
  );
};

export default BlurryOverlay;